import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Image as ImageIcon, Save, MapPin, Music, Plus, Trash2, Edit2, Check, X, ChevronUp, ChevronDown, Navigation, Loader2, Info, AlertTriangle, Map, GripVertical, Search, MoveRight, Square, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import MapLocationPicker from '../components/MapLocationPicker';
import TourTrustAdmin from '../components/TourTrustAdmin';
import SplitMoveStopsDialog from '../components/SplitMoveStopsDialog';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditTourPage = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stops, setStops] = useState([]);
  const [editingStopId, setEditingStopId] = useState(null);
  const [showAddStop, setShowAddStop] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [radiiNeedUpdate, setRadiiNeedUpdate] = useState(false); // Track if radii optimization is suggested
  const [mapSelectedStopId, setMapSelectedStopId] = useState(null); // For map marker selection
  // Split / Move Stops feature
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedStopIds, setSelectedStopIds] = useState([]); // ordered list matching stop_number
  const [showSplitMove, setShowSplitMove] = useState(false);
  const [showMap, setShowMap] = useState(true); // Toggle map visibility
  const [draggedStopId, setDraggedStopId] = useState(null); // For drag-and-drop reordering
  const [dragOverStopId, setDragOverStopId] = useState(null); // Track which stop is being dragged over
  const [buildInfo, setBuildInfo] = useState(null); // Backend boot time + features marker
  const [newStop, setNewStop] = useState({
    title: '',
    latitude: '',
    longitude: '',
    radius_meters: 50,
    audio_url: '',
    is_intro: false,
    navigation_mode: 'walking',
    insert_position: 'end', // 'beginning', 'end', or a number for after specific stop
    // Hidden waypoints (secret GPS-triggered audio, not part of the tour sequence)
    is_hidden: false,
    hidden_kind: 'encourage', // 'encourage' | 'warn'
    toast_message: '',
    // Contextual eligibility (only meaningful when is_hidden=true)
    trigger_after_stop_number: null,
    expires_when_stop_number: null,
    expires_after_seconds: null,
    recovery_message: '',
  });
  const [tourData, setTourData] = useState({
    title: '',
    city: '',
    description: '',
    duration: '',
    base_price: '',
    group_addon_price: '',
    allow_additional_devices: true,
    preview_audio_url: '',
    main_audio_url: '',
    cover_image_url: '',
    tour_type: 'walking',
    // Express Tour fields
    express_enabled: false,
    express_price: '',
    express_duration: '',
    express_description: '',
    // Private Access
    is_private: false,
    access_code: '',
    is_unlisted: false,
    is_free: false,
  });

  // Calculate distance between two GPS coordinates in meters
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    const R = 6371000; // Earth's radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
  };

  // Find nearest stop and suggest max radius to avoid overlap
  const getRadiusSuggestion = (currentLat, currentLon, currentStopId = null) => {
    if (!currentLat || !currentLon) return { nearestDistance: null, suggestedMax: null, nearestStop: null };
    
    let nearestDistance = Infinity;
    let nearestStop = null;
    
    for (const stop of stops) {
      // Skip the current stop being edited
      if (stop.stop_id === currentStopId) continue;
      // Skip intro stops (they don't have GPS)
      if (stop.is_intro || !stop.latitude || !stop.longitude) continue;
      
      const distance = calculateDistance(currentLat, currentLon, stop.latitude, stop.longitude);
      if (distance && distance < nearestDistance) {
        nearestDistance = distance;
        nearestStop = stop;
      }
    }
    
    if (nearestDistance === Infinity) {
      return { nearestDistance: null, suggestedMax: null, nearestStop: null };
    }
    
    // Suggest max radius as half the distance to nearest stop (to avoid overlap)
    const suggestedMax = Math.floor(nearestDistance / 2);
    
    return { nearestDistance, suggestedMax, nearestStop };
  };

  // Check if radius overlaps with nearby stops
  const checkRadiusOverlap = (lat, lon, radius, currentStopId = null) => {
    const overlaps = [];
    
    for (const stop of stops) {
      if (stop.stop_id === currentStopId) continue;
      if (stop.is_intro || !stop.latitude || !stop.longitude) continue;
      
      const distance = calculateDistance(lat, lon, stop.latitude, stop.longitude);
      if (!distance) continue;
      
      const otherRadius = stop.radius_meters || 50;
      // Check if the two circles overlap
      if (distance < radius + otherRadius) {
        overlaps.push({
          stop,
          distance,
          overlapAmount: (radius + otherRadius) - distance
        });
      }
    }
    
    return overlaps;
  };

  // Auto-optimize all radii to avoid overlaps
  const autoOptimizeAllRadii = async () => {
    // Hidden waypoints are strategically placed and must NOT influence
    // the on-path stops' radii — exclude them from both the seed set
    // and the mapping.
    const gpsStops = stops.filter(s => !s.is_intro && !s.is_hidden && s.latitude && s.longitude);
    
    if (gpsStops.length < 2) {
      toast.info('Need at least 2 GPS stops to optimize radii');
      return;
    }
    
    const optimizedStops = stops.map(stop => {
      // Skip intro stops and hidden waypoints
      if (stop.is_intro || stop.is_hidden || !stop.latitude || !stop.longitude) {
        return stop;
      }
      
      // Find nearest other GPS stop
      let nearestDistance = Infinity;
      for (const other of gpsStops) {
        if (other.stop_id === stop.stop_id) continue;
        const distance = calculateDistance(
          parseFloat(stop.latitude), 
          parseFloat(stop.longitude), 
          parseFloat(other.latitude), 
          parseFloat(other.longitude)
        );
        if (distance && distance < nearestDistance) {
          nearestDistance = distance;
        }
      }
      
      // Set radius to half the distance to nearest stop (minimum 20m)
      const suggestedRadius = nearestDistance === Infinity ? 50 : Math.max(20, Math.floor(nearestDistance / 2));
      
      return { ...stop, radius_meters: suggestedRadius };
    });
    
    setStops(optimizedStops);
    
    // Save all optimized stops to backend
    try {
      for (const stop of optimizedStops) {
        if (!stop.is_intro && stop.latitude && stop.longitude) {
          await axios.put(`${API}/admin/stops/${stop.stop_id}`, {
            title: stop.title,
            latitude: parseFloat(stop.latitude),
            longitude: parseFloat(stop.longitude),
            radius_meters: stop.radius_meters,
            audio_url: stop.audio_url || null,
            audio_type: stop.audio_url ? 'external' : 'none',
            is_intro: stop.is_intro || false,
            navigation_mode: stop.navigation_mode || 'walking',
          });
        }
      }
      toast.success(`Optimized radii for ${gpsStops.length} stops to prevent overlap!`);
      setRadiiNeedUpdate(false); // Clear the update flag after optimization
    } catch (error) {
      console.error('Error saving optimized radii:', error);
      toast.error('Failed to save some optimized radii');
    }
  };

  useEffect(() => {
    fetchTour();
    fetchStops();
    // Fetch the deploy marker so the admin can eyeball whether the current
    // build includes recent fixes. Silent-fail — the endpoint is optional.
    axios.get(`${API}/build-info`).then((r) => setBuildInfo(r.data)).catch(() => {});
  }, [tourId]);

  const fetchTour = async () => {
    try {
      // Use the admin endpoint where available so we can pre-fill the access code.
      let data;
      try {
        const adminRes = await axios.get(`${API}/admin/tours/${tourId}`);
        data = adminRes.data;
      } catch (_) {
        const response = await axios.get(`${API}/tours/${tourId}`);
        data = response.data;
      }
      setTourData({
        title: data.title,
        city: data.city,
        description: data.description,
        duration: data.duration,
        base_price: data.base_price,
        group_addon_price: data.group_addon_price || 0,
        allow_additional_devices: data.allow_additional_devices === true,
        preview_audio_url: data.preview_audio_url || '',
        main_audio_url: data.main_audio_url || '',
        cover_image_url: data.cover_image_url || '',
        tour_type: data.tour_type || 'walking',
        // Express Tour fields
        express_enabled: data.express_enabled || false,
        express_price: data.express_price || '',
        express_duration: data.express_duration || '',
        express_description: data.express_description || '',
        // Private Access
        is_private: !!data.is_private,
        access_code: data.access_code || '',
        is_unlisted: !!data.is_unlisted,
        is_free: !!data.is_free,
      });
    } catch (error) {
      toast.error('Failed to load tour');
    } finally {
      setLoading(false);
    }
  };

  const fetchStops = async () => {
    try {
      const response = await axios.get(`${API}/admin/tours/${tourId}/stops`);
      setStops(response.data);
    } catch (error) {
      console.error('Failed to load stops');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTourData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/image/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTourData((prev) => ({
        ...prev,
        cover_image_url: `${BACKEND_URL}${response.data.image_url}`,
      }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const saveTour = async () => {
    if (!tourData.title || !tourData.city) {
      toast.error('Please fill in tour title and city');
      return;
    }

    setSaving(true);
    try {
      // Build payload with proper type conversions
      const payload = {
        title: tourData.title,
        city: tourData.city,
        description: tourData.description || '',
        duration: tourData.duration || '',
        base_price: parseFloat(tourData.base_price) || 0,
        group_addon_price: parseFloat(tourData.group_addon_price) || 0,
        allow_additional_devices: tourData.allow_additional_devices,
        preview_audio_url: tourData.preview_audio_url || null,
        main_audio_url: tourData.main_audio_url || null,
        cover_image_url: tourData.cover_image_url || null,
        tour_type: tourData.tour_type || 'walking',
        express_enabled: tourData.express_enabled || false,
        express_price: tourData.express_price ? parseFloat(tourData.express_price) : null,
        express_duration: tourData.express_duration || null,
        express_description: tourData.express_description || null,
        is_private: !!tourData.is_private,
        access_code: tourData.is_private ? (tourData.access_code || '').trim() : '',
        is_unlisted: !!tourData.is_unlisted,
        is_free: !!tourData.is_free,
      };
      
      console.log('Updating tour with payload:', payload);
      await axios.put(`${API}/admin/tours/${tourId}`, payload);

      toast.success('Tour updated successfully');
      navigate('/admin');
    } catch (error) {
      console.error('Failed to update tour:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to update tour');
    } finally {
      setSaving(false);
    }
  };

  const handleAddStop = async () => {
    // For intro stops, GPS is optional. For regular stops, GPS is required.
    if (!newStop.title) {
      toast.error('Please fill in the stop title');
      return;
    }
    if (!newStop.is_intro && (!newStop.latitude || !newStop.longitude)) {
      toast.error('Please fill in GPS coordinates (or mark as Intro to skip)');
      return;
    }

    try {
      // Calculate the new stop_number based on insert position. Hidden
      // waypoints live OUTSIDE the numbered sequence — they get a high
      // stop_number (starting at 10000) so they naturally sort after all
      // visible stops in the DB and never disrupt the traveler-facing
      // numbering. This is also what makes them safe to skip during the
      // "renumber subsequent stops" step below.
      let newStopNumber;
      if (newStop.is_hidden) {
        const existingHidden = stops.filter(s => s.is_hidden);
        const maxHiddenNum = existingHidden.reduce(
          (max, s) => Math.max(max, s.stop_number || 0),
          9999,
        );
        newStopNumber = maxHiddenNum + 1;
      } else if (newStop.insert_position === 'beginning') {
        newStopNumber = 0;
      } else if (newStop.insert_position === 'end') {
        // Append after the last VISIBLE stop; hidden waypoints don't count.
        newStopNumber = stops.filter(s => !s.is_hidden).length;
      } else {
        // Insert after a specific stop
        newStopNumber = parseInt(newStop.insert_position) + 1;
      }

      const stopData = {
        ...newStop,
        stop_number: newStopNumber,
        latitude: newStop.latitude ? parseFloat(newStop.latitude) : null,
        longitude: newStop.longitude ? parseFloat(newStop.longitude) : null,
        radius_meters: parseFloat(newStop.radius_meters) || 50,
        audio_type: newStop.audio_url ? 'external' : 'none',
      };
      console.log('Sending stop data:', JSON.stringify(stopData, null, 2));
      
      // First, add the new stop
      await axios.post(`${API}/admin/tours/${tourId}/stops`, stopData);
      
      // If inserting a VISIBLE stop mid-sequence, shift subsequent visible
      // stops up by one. Hidden waypoints are skipped — they live in their
      // own 10000+ number range and stay put.
      if (!newStop.is_hidden && newStop.insert_position !== 'end') {
        const stopsToUpdate = stops
          .filter(s => !s.is_hidden)
          .filter((_, index) => index >= newStopNumber);
        for (const stop of stopsToUpdate) {
          await axios.put(`${API}/admin/stops/${stop.stop_id}`, {
            ...stop,
            stop_number: stop.stop_number + 1,
          });
        }
      }
      
      toast.success('Stop added successfully');
      setNewStop({ title: '', latitude: '', longitude: '', radius_meters: 50, audio_url: '', is_intro: false, navigation_mode: 'walking', insert_position: 'end', is_hidden: false, hidden_kind: 'encourage', toast_message: '', trigger_after_stop_number: null, expires_when_stop_number: null, expires_after_seconds: null, recovery_message: '' });
      setShowAddStop(false);
      fetchStops();
      // Flag that radii may need updating due to new stop
      if (!newStop.is_intro && newStop.latitude && newStop.longitude) {
        setRadiiNeedUpdate(true);
      }
    } catch (error) {
      console.error('Error adding stop:', error);
      toast.error('Failed to add stop');
    }
  };

  const handleUpdateStop = async (stop) => {
    try {
      // Normalize timecode fields: empty string means "use fallback".
      const normalizeSecs = (v) => {
        if (v === '' || v === undefined || v === null) return null;
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n >= 0 ? n : null;
      };
      const directions = normalizeSecs(stop.directions_start_seconds);
      const expressDirections = normalizeSecs(stop.express_directions_start_seconds);
      const updateData = {
        title: stop.title,
        latitude: stop.latitude ? parseFloat(stop.latitude) : null,
        longitude: stop.longitude ? parseFloat(stop.longitude) : null,
        radius_meters: parseFloat(stop.radius_meters) || 50,
        audio_url: stop.audio_url || null,
        audio_type: stop.audio_url ? 'external' : 'none',
        is_intro: stop.is_intro || false,
        navigation_mode: stop.navigation_mode || 'walking',
        include_in_express: stop.include_in_express || false,
        express_audio_url: stop.express_audio_url || null,
        directions_start_seconds: directions,
        express_directions_start_seconds: expressDirections,
        // Hidden waypoint fields — safe to always send (defaults to false/null)
        is_hidden: stop.is_hidden || false,
        hidden_kind: stop.is_hidden ? (stop.hidden_kind || 'encourage') : null,
        toast_message: (stop.toast_message || '').trim() || null,
        // Contextual eligibility for hidden waypoints (null if not hidden)
        trigger_after_stop_number: stop.is_hidden ? (stop.trigger_after_stop_number ?? null) : null,
        expires_when_stop_number: stop.is_hidden ? (stop.expires_when_stop_number ?? null) : null,
        expires_after_seconds: stop.is_hidden ? (stop.expires_after_seconds ?? null) : null,
        recovery_message: stop.is_hidden ? ((stop.recovery_message || '').trim() || null) : null,
      };
      console.log('Updating stop with data:', JSON.stringify(updateData, null, 2));
      await axios.put(`${API}/admin/stops/${stop.stop_id}`, updateData);
      toast.success('Stop updated successfully');
      setEditingStopId(null);
      fetchStops();
    } catch (error) {
      console.error('Error updating stop:', error);
      toast.error('Failed to update stop');
    }
  };

  const getCurrentLocation = (forNewStop = true) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (forNewStop) {
          setNewStop(prev => ({
            ...prev,
            latitude: latitude.toFixed(6),
            longitude: longitude.toFixed(6),
          }));
        }
        toast.success(`Location captured: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
        setGettingLocation(false);
      },
      (error) => {
        let message = 'Failed to get location';
        if (error.code === 1) message = 'Location access denied. Please enable location permissions.';
        else if (error.code === 2) message = 'Location unavailable. Please try again.';
        else if (error.code === 3) message = 'Location request timed out. Please try again.';
        toast.error(message);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const getCurrentLocationForStop = (stopId) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const updatedStops = stops.map(s =>
          s.stop_id === stopId
            ? { ...s, latitude: latitude.toFixed(6), longitude: longitude.toFixed(6) }
            : s
        );
        setStops(updatedStops);
        toast.success(`Location captured: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
        setGettingLocation(false);
      },
      (error) => {
        toast.error('Failed to get location');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Handle location change from Map Picker (drag or click)
  const handleStopLocationChange = (stopId, lat, lng) => {
    // Check if this is for a new stop being added
    if (stopId === 'new-stop') {
      setNewStop(prev => ({
        ...prev,
        latitude: lat,
        longitude: lng,
      }));
      setMapSelectedStopId(null);
      toast.success(`New stop location set: ${lat}, ${lng}`);
      // Scroll back to the add stop form
      document.querySelector('[data-testid="new-stop-title"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    const updatedStops = stops.map(s =>
      s.stop_id === stopId
        ? { ...s, latitude: lat, longitude: lng }
        : s
    );
    setStops(updatedStops);
    toast.success(`Location updated: ${lat}, ${lng}`);
    // Flag that radii may need updating due to position change
    setRadiiNeedUpdate(true);
  };

  // Handle stop selection from map marker click
  const handleMapStopSelect = (stopId) => {
    setMapSelectedStopId(stopId);
    // Also scroll to and highlight the stop in the list
    const stopElement = document.getElementById(`stop-${stopId}`);
    if (stopElement) {
      stopElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const moveStop = async (stopId, direction) => {
    // Reorder ONLY within the same group. Visible stops and hidden
    // waypoints have separate ordering — visible get sequential numbers,
    // hidden get letters (a, b, c, ...). A visible stop can never swap
    // places with a hidden waypoint.
    const source = stops.find(s => s.stop_id === stopId);
    if (!source) return;
    const isHidden = !!source.is_hidden;
    const group = stops
      .filter(s => !!s.is_hidden === isHidden)
      .sort((a, b) => (a.stop_number || 0) - (b.stop_number || 0));
    const idxInGroup = group.findIndex(s => s.stop_id === stopId);
    if (idxInGroup === -1) return;
    const targetIdx = direction === 'up' ? idxInGroup - 1 : idxInGroup + 1;
    if (targetIdx < 0 || targetIdx >= group.length) return;

    // Swap positions in the ordered array, then send the whole ordering to
    // the atomic reorder endpoint. One write, one truth.
    const reordered = [...group];
    [reordered[idxInGroup], reordered[targetIdx]] = [reordered[targetIdx], reordered[idxInGroup]];

    // Optimistic local update so the UI feels snappy.
    const orderedIds = reordered.map(s => s.stop_id);
    const newStops = stops.map(s => {
      const newIdx = orderedIds.indexOf(s.stop_id);
      if (newIdx === -1) return s;
      const base = isHidden ? 10000 : 0;
      return { ...s, stop_number: base + newIdx + 1 };
    });
    setStops(newStops);

    const affectsRadii = !isHidden && (
      (source.latitude && !source.is_intro) ||
      (group[targetIdx].latitude && !group[targetIdx].is_intro)
    );

    try {
      await axios.post(`${API}/admin/tours/${tourId}/reorder-stops`, {
        ordered_stop_ids: orderedIds,
        is_hidden: isHidden,
      });
      toast.success('Stop order updated');
      await fetchStops();
      if (affectsRadii) setRadiiNeedUpdate(true);
    } catch (error) {
      console.error('Failed to reorder stops:', error);
      toast.error(error?.response?.data?.detail || 'Failed to reorder stops');
      await fetchStops();
    }
  };

  // Letter label for hidden waypoints: 0→a, 1→b, ..., 25→z, 26→aa, 27→bb, ...
  // At the end of the alphabet the letter repeats (aa, bb, cc, ..., zz), then
  // triples (aaa, bbb, ...) — an admin-friendly, monotonically-increasing scheme
  // that works for hundreds of hidden waypoints per tour.
  const indexToLetterLabel = (n) => {
    const letter = String.fromCharCode(97 + (n % 26));
    const repeat = Math.floor(n / 26) + 1;
    return letter.repeat(repeat);
  };

  // Drag and drop handlers for reordering stops
  const handleDragStart = (e, stopId) => {
    setDraggedStopId(stopId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', stopId);
    // Add a slight delay to allow the drag image to be captured
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedStopId(null);
    setDragOverStopId(null);
  };

  const handleDragOver = (e, stopId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (stopId !== draggedStopId) {
      setDragOverStopId(stopId);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the stop element entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStopId(null);
    }
  };

  const handleDrop = async (e, targetStopId) => {
    e.preventDefault();
    setDragOverStopId(null);
    
    if (!draggedStopId || draggedStopId === targetStopId) {
      setDraggedStopId(null);
      return;
    }

    const draggedStop = stops.find(s => s.stop_id === draggedStopId);
    const targetStop = stops.find(s => s.stop_id === targetStopId);

    if (!draggedStop || !targetStop) {
      setDraggedStopId(null);
      return;
    }

    // Group-scoped drag: visible ↔ visible only, hidden ↔ hidden only.
    // Prevents accidentally dragging a hidden waypoint into the numbered
    // sequence — which was the root cause of the "last stop jumps to
    // second-to-last" bug (hidden waypoint got renumbered into visible
    // range, pushing the true last stop down by one).
    if (!!draggedStop.is_hidden !== !!targetStop.is_hidden) {
      setDraggedStopId(null);
      toast.info('Hidden waypoints and numbered stops are reordered separately');
      return;
    }

    const isHidden = !!draggedStop.is_hidden;
    const group = stops
      .filter(s => !!s.is_hidden === isHidden)
      .sort((a, b) => (a.stop_number || 0) - (b.stop_number || 0));
    const draggedIndex = group.findIndex(s => s.stop_id === draggedStopId);
    const targetIndex = group.findIndex(s => s.stop_id === targetStopId);
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedStopId(null);
      return;
    }

    // Reorder within the group only
    const reordered = [...group];
    const [dragged] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, dragged);

    // Atomic server-side renumber. Replaces the previous "24 concurrent
    // PUTs" pattern that was subject to partial writes and race conditions,
    // and manifested as "wrong stop jumps to the end after drag".
    try {
      await axios.post(`${API}/admin/tours/${tourId}/reorder-stops`, {
        ordered_stop_ids: reordered.map(s => s.stop_id),
        is_hidden: isHidden,
      });
      toast.success('Stops reordered successfully');
      fetchStops();
      // Only visible-stop drags affect walking-path radii
      if (!isHidden) setRadiiNeedUpdate(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to reorder stops');
      fetchStops();
    }

    setDraggedStopId(null);
  };

  const handleDeleteStop = async (stopId) => {
    const confirmed = window.confirm('Are you sure you want to delete this stop?');
    if (!confirmed) return;

    // Check if this was a GPS stop
    const deletedStop = stops.find(s => s.stop_id === stopId);
    const wasGpsStop = deletedStop && !deletedStop.is_intro && deletedStop.latitude && deletedStop.longitude;

    try {
      await axios.delete(`${API}/admin/stops/${stopId}`);
      toast.success('Stop deleted');
      fetchStops();
      // Flag that radii may need updating if a GPS stop was deleted
      if (wasGpsStop) {
        setRadiiNeedUpdate(true);
      }
    } catch (error) {
      toast.error('Failed to delete stop');
    }
  };

  const handleSanitizeStopNumbers = async () => {
    const confirmed = window.confirm(
      'Sanitize stop numbering?\n\n' +
      'This re-numbers visible stops sequentially (1..N) and hidden waypoints in the reserved range. ' +
      'It fixes tours where drag-and-drop or stale UI corrupted stop_numbers.\n\n' +
      'Order is preserved from current stop_number. Safe to run — it is idempotent.'
    );
    if (!confirmed) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/tours/${tourId}/sanitize-stop-numbers`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Renumbered ${data.visible_count} visible + ${data.hidden_count} hidden stops`);
      fetchStops();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to sanitize stop numbers');
    }
  };

  const [diagnoseResult, setDiagnoseResult] = useState(null);
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);

  const handleDiagnoseTour = async () => {
    setDiagnoseLoading(true);
    setDiagnoseResult(null);
    try {
      const { data } = await axios.get(`${API}/admin/tours/${tourId}/diagnose`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDiagnoseResult(data);
      if (data.verdict === 'healthy') {
        toast.success('Tour looks healthy — no data issues detected.');
      } else {
        toast.error(`Found ${data.issues.length} issue(s). See details below.`, { duration: 8000 });
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Diagnostic failed');
    } finally {
      setDiagnoseLoading(false);
    }
  };

  const handleStopAudioUpload = async (e, stopId) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/audio/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const audioUrl = `${BACKEND_URL}${response.data.audio_url}`;
      
      // Update the stop with the new audio URL
      const stop = stops.find(s => s.stop_id === stopId);
      if (stop) {
        await handleUpdateStop({ ...stop, audio_url: audioUrl });
      }
      toast.success('Audio uploaded and linked to stop');
    } catch (error) {
      toast.error('Failed to upload audio');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20">
      <header className="bg-cyan-500 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            data-testid="back-to-admin-button"
            className="flex items-center text-white hover:text-cyan-100 mb-4 transition-colors font-bold uppercase tracking-wide"
          >
            <ArrowLeft className="w-6 h-6 mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-4xl font-black uppercase">Edit Tour</h1>
          <p className="text-cyan-100 text-lg mt-2">{tourData.title}</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white border-4 border-cyan-500 rounded-2xl shadow-card p-8 mb-6">
          <h2 className="text-2xl font-black uppercase text-gray-900 mb-6">Cover Image</h2>
          
          {tourData.cover_image_url && (
            <div className="mb-4">
              <img
                src={tourData.cover_image_url}
                alt="Tour cover"
                className="w-full h-64 object-cover rounded-xl border-2 border-cyan-300"
              />
            </div>
          )}

          <label className="flex flex-col items-center justify-center w-full px-4 py-8 border-4 border-dashed border-cyan-400 rounded-xl hover:border-blue-500 transition-colors cursor-pointer bg-cyan-50">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              data-testid="cover-image-upload"
              className="hidden"
            />
            <div className="text-center">
              <ImageIcon className="w-12 h-12 text-cyan-500 mx-auto mb-3" />
              <span className="text-lg font-bold text-gray-900">
                {uploading ? 'Uploading...' : tourData.cover_image_url ? 'Change cover image' : 'Upload cover image'}
              </span>
              <p className="text-sm text-gray-600 mt-2">JPG, PNG, or WebP</p>
            </div>
          </label>
        </div>

        <div className="bg-white border-4 border-cyan-500 rounded-2xl shadow-card p-8 mb-6">
          <h2 className="text-2xl font-black uppercase text-gray-900 mb-6">Tour Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tour Title *</label>
              <input
                type="text"
                name="title"
                value={tourData.title}
                onChange={handleInputChange}
                data-testid="tour-title-input"
                className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all font-body"
                placeholder="e.g., Early Tel Aviv Walking Tour"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
              <input
                type="text"
                name="city"
                value={tourData.city}
                onChange={handleInputChange}
                data-testid="tour-city-input"
                className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all font-body"
                placeholder="e.g., Tel Aviv"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
              <textarea
                name="description"
                value={tourData.description}
                onChange={handleInputChange}
                data-testid="tour-description-input"
                className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all font-body"
                rows="6"
                placeholder="Describe your tour in detail..."
              ></textarea>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Duration *</label>
                <input
                  type="text"
                  name="duration"
                  value={tourData.duration}
                  onChange={handleInputChange}
                  data-testid="tour-duration-input"
                  className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all font-body"
                  placeholder="e.g., 60 minutes"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Base Price ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  name="base_price"
                  value={tourData.base_price}
                  onChange={handleInputChange}
                  data-testid="tour-base-price-input"
                  className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all font-body"
                  placeholder="15.00"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Device Price per 3 ($)</label>
              <input
                type="number"
                step="0.01"
                name="group_addon_price"
                value={tourData.group_addon_price}
                onChange={handleInputChange}
                data-testid="tour-device-price-input"
                className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all font-body"
                placeholder="5.00"
                disabled={!tourData.allow_additional_devices}
              />
              <p className="text-sm text-gray-600 mt-2">💡 Price charged for every 3 additional devices</p>
              
              {/* Toggle for Additional Devices */}
              <div className="mt-4 flex items-center gap-3 p-3 bg-slate-100 rounded-lg">
                <input
                  type="checkbox"
                  id="allow-additional-devices"
                  checked={tourData.allow_additional_devices}
                  onChange={(e) => setTourData({ ...tourData, allow_additional_devices: e.target.checked })}
                  className="w-5 h-5 text-cyan-600 rounded"
                  data-testid="allow-devices-checkbox"
                />
                <label htmlFor="allow-additional-devices" className="text-sm font-semibold text-gray-700">
                  Offer additional devices for this tour
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Uncheck to hide the "Add More Devices" option from customers
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tour Type *</label>
              <select
                name="tour_type"
                value={tourData.tour_type}
                onChange={handleInputChange}
                data-testid="tour-type-select"
                className="w-full px-4 py-3 rounded-lg border-2 border-cyan-300 focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all font-body"
              >
                <option value="walking">🚶 Walking Tour</option>
                <option value="driving">🚗 Driving Tour</option>
                <option value="mixed">🚶🚗 Mixed (Walking + Driving)</option>
              </select>
              <p className="text-sm text-gray-600 mt-2">💡 This determines how users are guided between stops</p>
            </div>
          </div>
        </div>

        {/* Access & Pricing — private (invitation-only), unlisted, free */}
        <div className="bg-white rounded-2xl shadow-card p-6 mb-6 border-4 border-slate-900">
          <h2 className="text-2xl font-black uppercase text-slate-900 mb-2">Access &amp; Pricing</h2>
          <p className="text-sm text-slate-600 mb-5">
            For invitation-only tours, pilots, VIP previews, museum demonstrations and live pitches.
          </p>

          <div className="mb-4 flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <input
              type="checkbox"
              id="edit-tour-is-free"
              checked={!!tourData.is_free}
              onChange={(e) => setTourData({ ...tourData, is_free: e.target.checked })}
              className="w-5 h-5 text-emerald-600 rounded"
              data-testid="tour-is-free"
            />
            <label htmlFor="edit-tour-is-free" className="flex-1">
              <div className="font-bold text-emerald-900">Free tour</div>
              <div className="text-xs text-emerald-700">No payment screen — users go straight into the tour after entering any required access code.</div>
            </label>
          </div>

          <div className="mb-4 flex items-center gap-3 p-4 bg-slate-900 text-white rounded-xl">
            <input
              type="checkbox"
              id="edit-tour-is-private"
              checked={!!tourData.is_private}
              onChange={(e) => setTourData({ ...tourData, is_private: e.target.checked })}
              className="w-5 h-5 text-blue-500 rounded"
              data-testid="tour-is-private"
            />
            <label htmlFor="edit-tour-is-private" className="flex-1">
              <div className="font-bold">Private Access — Access Code Required</div>
              <div className="text-xs text-slate-300">Users must enter a code on a dedicated screen before they can preview or start this tour.</div>
            </label>
          </div>

          {tourData.is_private && (
            <div className="mb-4 pl-4 border-l-2 border-blue-500" data-testid="access-code-section">
              <label className="block text-sm font-bold text-slate-800 mb-2 uppercase tracking-wider">
                Access code
              </label>
              <input
                type="text"
                value={tourData.access_code || ''}
                onChange={(e) => setTourData({ ...tourData, access_code: e.target.value })}
                placeholder="e.g. AUDIOSTRIDE2026"
                data-testid="tour-access-code"
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-mono uppercase tracking-wider"
                autoComplete="off"
              />
              <p className="text-xs text-slate-500 mt-2">
                Stakeholders enter this code on the tour's landing page. You can change it anytime — old codes will stop working immediately.
              </p>
            </div>
          )}

          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <input
              type="checkbox"
              id="edit-tour-is-unlisted"
              checked={!!tourData.is_unlisted}
              onChange={(e) => setTourData({ ...tourData, is_unlisted: e.target.checked })}
              className="w-5 h-5 text-amber-600 rounded"
              data-testid="tour-is-unlisted"
            />
            <label htmlFor="edit-tour-is-unlisted" className="flex-1">
              <div className="font-bold text-amber-900">Unlisted — hide from public discovery</div>
              <div className="text-xs text-amber-700">Tour is still reachable via direct link, but won't appear in the homepage tour list. Useful for true invitation-only experiences.</div>
            </label>
          </div>
        </div>

        {/* TRUST & SOCIAL PROOF SECTION */}
        <TourTrustAdmin tourId={tourId} token={token} />

        {/* EXPRESS TOUR SECTION */}
        <div className="bg-white border-4 border-amber-400 rounded-2xl shadow-card p-8 mb-6">
          <h2 className="text-2xl font-black uppercase text-gray-900 mb-6 flex items-center">
            <span className="mr-3">⚡</span>
            Express Tour
          </h2>
          {/* Enable Express Toggle */}
          <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl mb-6">
            <input
              type="checkbox"
              id="express-enabled"
              checked={tourData.express_enabled}
              onChange={(e) => setTourData({ ...tourData, express_enabled: e.target.checked })}
              className="w-6 h-6 text-amber-600 rounded"
              data-testid="express-enabled-checkbox"
            />
            <label htmlFor="express-enabled" className="text-lg font-bold text-gray-800">
              Enable Express Tour Version
            </label>
          </div>
          
          <p className="text-gray-600 mb-6">
            Create a shorter "highlights" version of this tour for visitors with less time. 
            Select which stops to include below when editing each stop.
          </p>
          
          {tourData.express_enabled && (
            <div className="space-y-4 border-t-2 border-amber-200 pt-6">
              {/* Legacy express_price field removed from UI. Customers buy
                  the full tour and get BOTH versions for 7 days — there is no
                  separate express purchase path. */}
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900" data-testid="express-included-banner">
                <strong>Both versions included in one purchase.</strong> Buyers get the full tour + express highlights for 7 days — there is no separate express price.
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Express Duration</label>
                <input
                  type="text"
                  name="express_duration"
                  value={tourData.express_duration}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border-2 border-amber-300 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all"
                  placeholder="e.g., 30 minutes"
                  data-testid="express-duration-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Express Description (optional)</label>
                <textarea
                  name="express_description"
                  value={tourData.express_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 rounded-lg border-2 border-amber-300 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all"
                  placeholder="A quick highlights tour for visitors short on time..."
                  data-testid="express-description-input"
                />
              </div>
              
              {/* Express stops count */}
              <div className="bg-amber-100 rounded-lg p-4">
                <p className="text-amber-800 font-semibold">
                  ⚡ Express Stops: {stops.filter(s => s.include_in_express).length} of {stops.length} selected
                </p>
                <p className="text-amber-600 text-sm mt-1">
                  Mark stops for the express tour using the "⚡ Include in Express" checkbox when editing each stop below.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border-4 border-blue-400 rounded-2xl shadow-card p-8 mb-6">
          <h2 className="text-2xl font-black uppercase text-gray-900 mb-6 flex items-center">
            <Music className="w-6 h-6 mr-3 text-blue-500" />
            Audio Files
          </h2>
          <div className="space-y-6">
            {/* Preview Audio Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Preview Audio (plays on tour page before purchase)</label>
              
              {!tourData.preview_audio_url ? (
                <label className="w-full bg-blue-500 hover:bg-blue-600 text-white px-6 py-4 rounded-xl font-bold cursor-pointer flex items-center justify-center transition-all text-lg">
                  <Upload className="w-6 h-6 mr-3" />
                  {uploading ? 'Uploading...' : 'Upload Preview MP3'}
                  <input
                    type="file"
                    accept="audio/*,.mp3,.wav,.m4a,.ogg"
                    disabled={uploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      setUploading(true);
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        const response = await axios.post(`${API}/admin/audio/upload`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        const audioUrl = `${BACKEND_URL}${response.data.audio_url}`;
                        setTourData({ ...tourData, preview_audio_url: audioUrl });
                        toast.success('Preview audio uploaded!');
                      } catch (error) {
                        toast.error('Failed to upload audio');
                      } finally {
                        setUploading(false);
                      }
                    }}
                    className="hidden"
                  />
                </label>
              ) : (
                <div className="bg-green-50 border-2 border-green-400 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-green-700 flex items-center">
                      <Music className="w-4 h-4 mr-2" />
                      Preview Audio Uploaded
                    </p>
                    <button
                      type="button"
                      onClick={() => setTourData({ ...tourData, preview_audio_url: '' })}
                      className="text-red-500 hover:text-red-700 text-sm font-bold"
                    >
                      Remove
                    </button>
                  </div>
                  <audio controls className="w-full">
                    <source src={tourData.preview_audio_url} type="audio/mpeg" />
                  </audio>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">💡 Short 30-60 second clip so users can hear what to expect</p>
            </div>

            {/* Main Audio Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Main Tour Audio (optional - full audio file)</label>
              
              {!tourData.main_audio_url ? (
                <label className="w-full bg-purple-500 hover:bg-purple-600 text-white px-6 py-4 rounded-xl font-bold cursor-pointer flex items-center justify-center transition-all text-lg">
                  <Upload className="w-6 h-6 mr-3" />
                  Upload Main Audio MP3
                  <input
                    type="file"
                    accept="audio/*,.mp3,.wav,.m4a,.ogg"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      setUploading(true);
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      try {
                        const response = await axios.post(`${API}/admin/audio/upload`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        const audioUrl = `${BACKEND_URL}${response.data.audio_url}`;
                        setTourData({ ...tourData, main_audio_url: audioUrl });
                        toast.success('Main audio uploaded!');
                      } catch (error) {
                        toast.error('Failed to upload audio');
                      } finally {
                        setUploading(false);
                      }
                    }}
                    className="hidden"
                  />
                </label>
              ) : (
                <div className="bg-purple-50 border-2 border-purple-400 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-purple-700 flex items-center">
                      <Music className="w-4 h-4 mr-2" />
                      Main Audio Uploaded
                    </p>
                    <button
                      type="button"
                      onClick={() => setTourData({ ...tourData, main_audio_url: '' })}
                      className="text-red-500 hover:text-red-700 text-sm font-bold"
                    >
                      Remove
                    </button>
                  </div>
                  <audio controls className="w-full">
                    <source src={tourData.main_audio_url} type="audio/mpeg" />
                  </audio>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">💡 Optional: Upload the complete tour audio if you have one file for the whole tour</p>
            </div>
          </div>
        </div>

        {/* Stops Management Section */}
        <div className="bg-white border-4 border-green-400 rounded-2xl shadow-card p-8 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-black uppercase text-gray-900 flex items-center">
              <MapPin className="w-6 h-6 mr-3 text-green-500" />
              Tour Stops ({stops.length})
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={autoOptimizeAllRadii}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full font-bold flex items-center transition-all active:scale-95"
                title="Automatically set all stop radii to avoid overlap"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Auto-Optimize Radii
              </button>
              <button
                onClick={() => {
                  const next = !selectionMode;
                  setSelectionMode(next);
                  if (!next) setSelectedStopIds([]);
                }}
                data-testid="toggle-selection-mode"
                className={`px-4 py-2 rounded-full font-bold flex items-center transition-all active:scale-95 ${
                  selectionMode
                    ? 'bg-slate-900 text-white hover:bg-slate-800'
                    : 'bg-white text-slate-700 border-2 border-slate-300 hover:bg-slate-50'
                }`}
                title="Select stops to move or split into another tour"
              >
                {selectionMode ? <CheckSquare className="w-5 h-5 mr-2" /> : <Square className="w-5 h-5 mr-2" />}
                {selectionMode ? 'Selecting…' : 'Select stops'}
              </button>
              <button
                onClick={() => setShowAddStop(!showAddStop)}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full font-bold flex items-center transition-all active:scale-95"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Stop
              </button>
            </div>
          </div>
          
          {/* Quick Start Guide */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6">
            <h4 className="font-bold text-blue-900 mb-2 flex items-center">
              <Info className="w-5 h-5 mr-2" />
              Quick Guide: Creating Tour Stops
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li><span className="font-semibold">🎙️ Intro Stop:</span> Plays from anywhere - perfect for tour introductions before users arrive at the first location. No GPS needed!</li>
              <li><span className="font-semibold">🚶 Walking Stop:</span> Users will walk to this location. Audio plays when they enter the GPS radius.</li>
              <li><span className="font-semibold">🚗 Driving Stop:</span> Users will drive to this location. Great for stops that are far apart.</li>
            </ul>
          </div>

          {/* Map Location Picker - Toggle */}
          <div className="mb-4">
            <button
              onClick={() => setShowMap(!showMap)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all ${
                showMap ? 'bg-cyan-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              data-testid="toggle-map-button"
            >
              <Map className="w-5 h-5" />
              {showMap ? 'Hide Map' : 'Show Map'}
            </button>
          </div>

          {/* Map Location Picker Component */}
          {showMap && stops.length > 0 && (
            <div className="mb-6" id="map-section">
              <MapLocationPicker
                stops={stops}
                selectedStopId={mapSelectedStopId}
                onStopLocationChange={handleStopLocationChange}
                onStopSelect={handleMapStopSelect}
                showAllStops={true}
              />
              {mapSelectedStopId && (
                <div className="mt-2 flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">
                    <strong>Selected:</strong> {stops.find(s => s.stop_id === mapSelectedStopId)?.title || 'Unknown'} 
                    <span className="text-xs ml-2">(Click on map to set new location, or drag the marker)</span>
                  </p>
                  <button
                    onClick={() => setMapSelectedStopId(null)}
                    className="text-red-600 hover:text-red-800 text-sm font-bold"
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Radii Update Warning Banner */}
          {radiiNeedUpdate && (
            <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-amber-800">Radii may need updating!</p>
                  <p className="text-sm text-amber-700">
                    You've added, deleted, or rearranged stops. The trigger radii may now overlap with nearby stops.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setRadiiNeedUpdate(false)}
                  className="text-amber-700 hover:text-amber-900 text-sm underline"
                >
                  Dismiss
                </button>
                <button
                  onClick={autoOptimizeAllRadii}
                  className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-full font-bold text-sm flex items-center transition-all active:scale-95"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Re-Optimize Now
                </button>
              </div>
            </div>
          )}

          {/* Add New Stop Form */}
          {showAddStop && (
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">Add New Stop</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Stop Title *</label>
                  <input
                    type="text"
                    value={newStop.title}
                    onChange={(e) => setNewStop({ ...newStop, title: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    placeholder="e.g., City Hall Plaza"
                    data-testid="new-stop-title"
                  />
                </div>
                
                {/* Insert Position Selector — irrelevant for hidden
                    waypoints since they don't participate in the numbered
                    sequence. Also hide it when only intro stops exist
                    (the option list would only show the intro). */}
                {!newStop.is_hidden && (
                <div className="col-span-2 bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <label className="block text-sm font-semibold text-purple-800 mb-2">
                    📍 Where to insert this stop?
                  </label>
                  <select
                    value={newStop.insert_position}
                    onChange={(e) => setNewStop({ ...newStop, insert_position: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-purple-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    data-testid="new-stop-position"
                  >
                    <option value="beginning">At the beginning (Stop #1)</option>
                    {stops.filter(s => !s.is_hidden).map((stop, index) => (
                      <option key={stop.stop_id} value={index}>
                        After Stop #{index + 1}: {stop.title}
                      </option>
                    ))}
                    <option value="end">At the end (Stop #{stops.filter(s => !s.is_hidden).length + 1})</option>
                  </select>
                  <p className="text-xs text-purple-600 mt-2">
                    Other stops will be renumbered automatically
                  </p>
                </div>
                )}
                
                {/* Intro Stop Toggle */}
                <div className="col-span-2 bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="new-stop-intro"
                      checked={newStop.is_intro || false}
                      onChange={(e) => setNewStop({ ...newStop, is_intro: e.target.checked, is_hidden: e.target.checked ? false : newStop.is_hidden })}
                      className="w-5 h-5 text-teal-600 rounded"
                      data-testid="new-stop-intro-checkbox"
                    />
                    <label htmlFor="new-stop-intro" className="text-sm font-semibold text-teal-800">
                      This is an Intro Stop (plays from anywhere - no GPS needed)
                    </label>
                  </div>
                  <p className="text-xs text-teal-600 mt-2 ml-8">
                    Perfect for tour introductions that users can listen to before arriving at the first physical location.
                  </p>
                </div>

                {/* Hidden Waypoint Toggle — secret GPS-triggered audio that isn't
                    in the public stops list, doesn't count toward progress,
                    and fires once per session. Great for on-route encouragement
                    or off-track warnings. Mutually exclusive with Intro. */}
                {!newStop.is_intro && (
                  <div className="col-span-2 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="new-stop-hidden"
                        checked={newStop.is_hidden || false}
                        onChange={(e) => setNewStop({ ...newStop, is_hidden: e.target.checked })}
                        className="w-5 h-5 text-indigo-600 rounded"
                        data-testid="new-stop-hidden-checkbox"
                      />
                      <label htmlFor="new-stop-hidden" className="text-sm font-semibold text-indigo-900">
                        🔒 Hidden waypoint (secret, not shown in the public stops list)
                      </label>
                    </div>
                    <p className="text-xs text-indigo-700 mt-2 ml-8">
                      Fires once when the user enters the GPS radius. Doesn&apos;t count toward tour progress or Strides. Users never see it in their list.
                    </p>
                    {newStop.is_hidden && (
                      <div className="ml-8 mt-3 space-y-3">
                        <div>
                          <label className="block text-xs font-semibold text-indigo-800 mb-1">Type</label>
                          <select
                            value={newStop.hidden_kind || 'encourage'}
                            onChange={(e) => setNewStop({ ...newStop, hidden_kind: e.target.value })}
                            data-testid="new-stop-hidden-kind"
                            className="w-full px-3 py-2 rounded-lg border border-indigo-300 text-sm bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                          >
                            <option value="encourage">✅ Encouragement — user is on the right track</option>
                            <option value="warn">⚠️ Off-track — user has taken a wrong turn</option>
                          </select>
                          <p className="text-xs text-indigo-600 mt-1">
                            {newStop.hidden_kind === 'warn'
                              ? 'Interrupts the current stop audio the moment the user enters this radius (safety-critical).'
                              : 'Waits politely — plays after the current stop finishes, or immediately if nothing is playing.'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-indigo-800 mb-1">
                            On-screen message (optional, ≤120 chars)
                          </label>
                          <input
                            type="text"
                            maxLength={120}
                            value={newStop.toast_message || ''}
                            onChange={(e) => setNewStop({ ...newStop, toast_message: e.target.value })}
                            placeholder={newStop.hidden_kind === 'warn'
                              ? "e.g., You may have taken a wrong turn — head back toward Rothschild Blvd."
                              : "e.g., You're on the right path — the next stop is just around the corner!"}
                            data-testid="new-stop-hidden-toast"
                            className="w-full px-3 py-2 rounded-lg border border-indigo-300 text-sm bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                          />
                          <p className="text-xs text-indigo-500 mt-1">
                            Shown as a subtle bottom-toast while the audio plays. Leave blank for audio-only.
                          </p>
                        </div>

                        {/* Contextual eligibility — the "no false alarms
                            on loop-backs" fields. All optional; blank =
                            legacy behavior (eligible from tour start, no
                            expiry). */}
                        <div className="pt-3 mt-2 border-t border-indigo-200 space-y-3">
                          <p className="text-[0.7rem] uppercase tracking-widest font-black text-indigo-700">
                            🎯 Eligibility window (optional)
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-semibold text-indigo-800 mb-1">Trigger AFTER</label>
                              <select
                                value={newStop.trigger_after_stop_number ?? ''}
                                onChange={(e) => setNewStop({
                                  ...newStop,
                                  trigger_after_stop_number: e.target.value === '' ? null : parseInt(e.target.value, 10),
                                })}
                                data-testid="new-stop-trigger-after"
                                className="w-full px-2 py-2 rounded-lg border border-indigo-300 text-xs bg-white"
                              >
                                <option value="">— Tour start (no gate) —</option>
                                {stops.filter(s => !s.is_hidden).map((s) => (
                                  <option key={s.stop_id} value={s.stop_number}>
                                    #{s.stop_number} {s.title}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-indigo-800 mb-1">Expires WHEN reached</label>
                              <select
                                value={newStop.expires_when_stop_number ?? ''}
                                onChange={(e) => setNewStop({
                                  ...newStop,
                                  expires_when_stop_number: e.target.value === '' ? null : parseInt(e.target.value, 10),
                                })}
                                data-testid="new-stop-expires-when"
                                className="w-full px-2 py-2 rounded-lg border border-indigo-300 text-xs bg-white"
                              >
                                <option value="">— Never expires by arrival —</option>
                                {stops.filter(s => !s.is_hidden).map((s) => (
                                  <option key={s.stop_id} value={s.stop_number}>
                                    #{s.stop_number} {s.title}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-semibold text-indigo-800 mb-1">Expires after (seconds)</label>
                              <input
                                type="number"
                                min="0"
                                step="30"
                                value={newStop.expires_after_seconds ?? ''}
                                onChange={(e) => setNewStop({
                                  ...newStop,
                                  expires_after_seconds: e.target.value === '' ? null : parseInt(e.target.value, 10),
                                })}
                                data-testid="new-stop-expires-after-seconds"
                                placeholder="e.g. 180 = 3 min"
                                className="w-full px-2 py-2 rounded-lg border border-indigo-300 text-xs bg-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-indigo-800 mb-1">If wrong stop reached (toast)</label>
                              <input
                                type="text"
                                maxLength={120}
                                value={newStop.recovery_message || ''}
                                onChange={(e) => setNewStop({ ...newStop, recovery_message: e.target.value })}
                                data-testid="new-stop-recovery-message"
                                placeholder="e.g., Head back — the next stop is Allenby"
                                className="w-full px-2 py-2 rounded-lg border border-indigo-300 text-xs bg-white"
                              />
                            </div>
                          </div>
                          <p className="text-[0.7rem] text-indigo-600 leading-snug">
                            Blank = no gating. Use these to prevent false alarms on loop-backs. Example: for a "wrong turn" warning between #7 and #8 — set <strong>Trigger AFTER: #7</strong> and <strong>Expires WHEN reached: #8</strong>.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* GPS Location Section - Hidden when Intro is checked */}
                {!newStop.is_intro && (
                  <>
                    <div className="col-span-2 bg-white border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-semibold text-gray-700">GPS Location *</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => getCurrentLocation(true)}
                            disabled={gettingLocation}
                            className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center transition-all active:scale-95"
                          >
                            {gettingLocation ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Getting...
                              </>
                            ) : (
                              <>
                                <Navigation className="w-4 h-4 mr-2" />
                                Use My Location
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              // Set a special mode to indicate we're setting location for new stop
                              setMapSelectedStopId('new-stop');
                              toast.info('Click on the map to set the location for this new stop');
                              // Scroll to map
                              document.querySelector('[data-testid="toggle-map-button"]')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center transition-all active:scale-95"
                          >
                            <Map className="w-4 h-4 mr-2" />
                            Set on Map
                          </button>
                        </div>
                      </div>
                      
                      {/* Indicator when map mode is active */}
                      {mapSelectedStopId === 'new-stop' && (
                        <div className="bg-cyan-100 border border-cyan-300 rounded-lg p-3 mb-3">
                          <p className="text-sm text-cyan-800 font-semibold flex items-center">
                            <Map className="w-4 h-4 mr-2" />
                            Map mode active! Scroll down and click on the map to set location.
                          </p>
                          <button
                            onClick={() => setMapSelectedStopId(null)}
                            className="text-cyan-600 hover:text-cyan-800 text-xs underline mt-1"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Latitude</label>
                          <input
                            type="number"
                            step="any"
                            value={newStop.latitude}
                            onChange={(e) => setNewStop({ ...newStop, latitude: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                            placeholder="32.0853"
                            data-testid="new-stop-latitude"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Longitude</label>
                          <input
                            type="number"
                            step="any"
                            value={newStop.longitude}
                            onChange={(e) => setNewStop({ ...newStop, longitude: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                            placeholder="34.7818"
                            data-testid="new-stop-longitude"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        📍 Tip: Click "Use My Current Location" while at the actual stop location!
                      </p>
                    </div>
                    
                    {/* Trigger Radius with Smart Suggestions */}
                    <div className="col-span-2 bg-white border border-green-200 rounded-lg p-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        📏 Trigger Radius (meters)
                      </label>
                      
                      {/* Smart radius suggestion based on nearby stops */}
                      {(() => {
                        const suggestion = getRadiusSuggestion(
                          parseFloat(newStop.latitude), 
                          parseFloat(newStop.longitude)
                        );
                        const overlaps = newStop.latitude && newStop.longitude 
                          ? checkRadiusOverlap(
                              parseFloat(newStop.latitude), 
                              parseFloat(newStop.longitude), 
                              parseInt(newStop.radius_meters) || 50
                            ) 
                          : [];
                        
                        return (
                          <>
                            {/* Suggestion Box */}
                            {suggestion.nearestStop && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                                <p className="text-sm text-blue-800">
                                  <strong>Nearest stop:</strong> "{suggestion.nearestStop.title}" is <strong>{suggestion.nearestDistance}m</strong> away
                                </p>
                                <p className="text-sm text-blue-600 mt-1">
                                  💡 <strong>Suggested max radius: {suggestion.suggestedMax}m</strong> to avoid overlap
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setNewStop({ ...newStop, radius_meters: suggestion.suggestedMax })}
                                  className="mt-2 text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-full"
                                >
                                  Use suggested radius
                                </button>
                              </div>
                            )}
                            
                            {/* Warning if radius overlaps */}
                            {overlaps.length > 0 && (
                              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-3">
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-semibold text-amber-800">Radius overlap detected!</p>
                                    <p className="text-xs text-amber-700 mt-1">
                                      This radius may trigger audio for nearby stops:
                                    </p>
                                    <ul className="text-xs text-amber-600 mt-1">
                                      {overlaps.map((o, i) => (
                                        <li key={i}>• "{o.stop.title}" ({o.distance}m away, overlaps by {o.overlapAmount}m)</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      
                      <div className="flex items-center gap-4">
                        <input
                          type="number"
                          value={newStop.radius_meters}
                          onChange={(e) => setNewStop({ ...newStop, radius_meters: parseInt(e.target.value) || 50 })}
                          className="w-32 px-4 py-2 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-center font-bold"
                          data-testid="new-stop-radius"
                        />
                        <span className="text-sm text-gray-600">
                          Audio will play when user is within <strong>{newStop.radius_meters || 50}m</strong> of this location
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        💡 Common values: 25-50m for precise spots, 50-100m for general areas, 100m+ for large open spaces
                      </p>
                    </div>
                  </>
                )}
                
                {/* Navigation Mode - Walking or Driving */}
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">How will users get to this stop?</label>
                  <select
                    value={newStop.navigation_mode || 'walking'}
                    onChange={(e) => setNewStop({ ...newStop, navigation_mode: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    data-testid="new-stop-navigation-mode"
                  >
                    <option value="walking">🚶 Walking - Users will walk to this stop</option>
                    <option value="driving">🚗 Driving - Users will drive to this stop</option>
                  </select>
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Audio URL (Optional - add later)</label>
                  <input
                    type="text"
                    value={newStop.audio_url}
                    onChange={(e) => setNewStop({ ...newStop, audio_url: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500 font-mono text-sm"
                    placeholder="https://example.com/audio.mp3"
                    data-testid="new-stop-audio-url"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleAddStop}
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full font-bold transition-all active:scale-95"
                  data-testid="add-stop-submit"
                >
                  Add Stop
                </button>
                <button
                  onClick={() => setShowAddStop(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-full font-bold transition-all active:scale-95"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Stops List — visible stops and hidden waypoints render as
              distinct groups. Visible get sequential numbers (1, 2, 3, …).
              Hidden waypoints get letter labels (a, b, c, …, aa, bb, cc, …)
              because they are OUT of the numbered sequence and shouldn't
              share the traveler-facing count. */}
          {stops.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No stops added yet. Click "Add Stop" to create your first stop.</p>
            </div>
          ) : (() => {
            // Split into groups + precompute display labels / group positions
            // so a single .map() below can render both sections without
            // duplicating the row markup.
            const visibleGroup = [...stops]
              .filter(s => !s.is_hidden)
              .sort((a, b) => (a.stop_number || 0) - (b.stop_number || 0));
            const hiddenGroup = [...stops]
              .filter(s => s.is_hidden)
              .sort((a, b) => (a.stop_number || 0) - (b.stop_number || 0));
            const orderedStops = [...visibleGroup, ...hiddenGroup];
            const meta = {};
            visibleGroup.forEach((s, i) => {
              meta[s.stop_id] = {
                label: String(i + 1),
                isFirstInGroup: i === 0,
                isLastInGroup: i === visibleGroup.length - 1,
                isHidden: false,
              };
            });
            hiddenGroup.forEach((s, i) => {
              meta[s.stop_id] = {
                label: indexToLetterLabel(i),
                isFirstInGroup: i === 0,
                isLastInGroup: i === hiddenGroup.length - 1,
                isHidden: true,
              };
            });
            return (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                <GripVertical className="w-4 h-4" />
                <span>Drag stops to reorder, or use the up/down arrows</span>
              </p>
              {orderedStops.map((stop) => {
                const m = meta[stop.stop_id];
                // Section divider before the first hidden waypoint
                const showDivider = m.isHidden && m.isFirstInGroup;
                return (
                <React.Fragment key={stop.stop_id}>
                {showDivider && (
                  <div className="pt-4 mt-4 border-t-2 border-dashed border-indigo-200" data-testid="hidden-waypoints-divider">
                    <div className="mb-3">
                      <h4 className="text-sm font-black uppercase tracking-wider text-indigo-700 flex items-center gap-2">
                        <span>🔒 Hidden Waypoints</span>
                        <span className="bg-indigo-100 text-indigo-700 text-[10px] px-2 py-0.5 rounded-full">
                          {hiddenGroup.length}
                        </span>
                      </h4>
                      <p className="text-xs text-slate-500 mt-1">
                        Not shown to travelers. GPS-triggered secrets — labeled with letters so they stay <strong>out of the numbered tour sequence</strong>. If a traveler doesn&apos;t reach one, it has no effect on the rest of the tour.
                      </p>
                    </div>
                  </div>
                )}
                <div
                  id={`stop-${stop.stop_id}`}
                  draggable={editingStopId !== stop.stop_id}
                  onDragStart={(e) => handleDragStart(e, stop.stop_id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, stop.stop_id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stop.stop_id)}
                  className={`border-2 rounded-xl p-4 transition-all ${
                    dragOverStopId === stop.stop_id
                      ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-300 scale-[1.02]'
                      : draggedStopId === stop.stop_id
                        ? 'opacity-50 border-gray-400'
                        : mapSelectedStopId === stop.stop_id 
                          ? 'border-red-500 bg-red-50 ring-2 ring-red-300' 
                          : m.isHidden
                            ? 'border-indigo-200 bg-indigo-50/40'
                          : stop.audio_url 
                            ? 'border-green-300 bg-green-50' 
                            : 'border-orange-300 bg-orange-50'
                  } ${editingStopId !== stop.stop_id ? 'cursor-grab active:cursor-grabbing' : ''}`}
                  onClick={() => setMapSelectedStopId(stop.stop_id)}
                >
                  {editingStopId === stop.stop_id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={stop.title}
                        onChange={(e) => {
                          const updatedStops = stops.map(s => 
                            s.stop_id === stop.stop_id ? { ...s, title: e.target.value } : s
                          );
                          setStops(updatedStops);
                        }}
                        className="w-full px-3 py-2 rounded border border-gray-300 font-bold"
                        placeholder="Stop title"
                      />
                      
                      {/* Intro & Navigation Mode - ALWAYS VISIBLE */}
                      <div className="bg-slate-100 rounded-lg p-3 space-y-3">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id={`intro-${stop.stop_id}`}
                            checked={stop.is_intro || false}
                            onChange={(e) => {
                              const updatedStops = stops.map(s => 
                                s.stop_id === stop.stop_id ? { ...s, is_intro: e.target.checked, is_hidden: e.target.checked ? false : s.is_hidden } : s
                              );
                              setStops(updatedStops);
                            }}
                            className="w-5 h-5 text-teal-600 rounded"
                          />
                          <label htmlFor={`intro-${stop.stop_id}`} className="text-sm font-semibold text-gray-700">
                            🎙️ Intro Stop (plays from anywhere - no GPS needed)
                          </label>
                        </div>

                        {/* Hidden Waypoint controls — mutually exclusive with Intro */}
                        {!stop.is_intro && (
                          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                id={`hidden-${stop.stop_id}`}
                                checked={stop.is_hidden || false}
                                onChange={(e) => {
                                  const updatedStops = stops.map(s =>
                                    s.stop_id === stop.stop_id ? { ...s, is_hidden: e.target.checked } : s
                                  );
                                  setStops(updatedStops);
                                }}
                                data-testid={`edit-hidden-checkbox-${stop.stop_id}`}
                                className="w-5 h-5 text-indigo-600 rounded"
                              />
                              <label htmlFor={`hidden-${stop.stop_id}`} className="text-sm font-semibold text-indigo-900">
                                🔒 Hidden waypoint (secret, not in the public list)
                              </label>
                            </div>
                            {stop.is_hidden && (
                              <div className="ml-8 space-y-2">
                                <select
                                  value={stop.hidden_kind || 'encourage'}
                                  onChange={(e) => {
                                    const updatedStops = stops.map(s =>
                                      s.stop_id === stop.stop_id ? { ...s, hidden_kind: e.target.value } : s
                                    );
                                    setStops(updatedStops);
                                  }}
                                  data-testid={`edit-hidden-kind-${stop.stop_id}`}
                                  className="w-full px-3 py-2 rounded border border-indigo-300 text-sm bg-white"
                                >
                                  <option value="encourage">✅ Encouragement — user is on the right track</option>
                                  <option value="warn">⚠️ Off-track — user has taken a wrong turn</option>
                                </select>
                                <input
                                  type="text"
                                  maxLength={120}
                                  value={stop.toast_message || ''}
                                  onChange={(e) => {
                                    const updatedStops = stops.map(s =>
                                      s.stop_id === stop.stop_id ? { ...s, toast_message: e.target.value } : s
                                    );
                                    setStops(updatedStops);
                                  }}
                                  placeholder="On-screen message (optional, ≤120 chars)"
                                  data-testid={`edit-hidden-toast-${stop.stop_id}`}
                                  className="w-full px-3 py-2 rounded border border-indigo-300 text-sm bg-white"
                                />
                                {/* Contextual eligibility fields for existing hidden waypoint */}
                                <div className="pt-2 mt-2 border-t border-indigo-200 space-y-2">
                                  <p className="text-[0.65rem] uppercase tracking-widest font-black text-indigo-700">
                                    🎯 Eligibility window
                                  </p>
                                  <div className="grid grid-cols-2 gap-2">
                                    <select
                                      value={stop.trigger_after_stop_number ?? ''}
                                      onChange={(e) => {
                                        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                        const updatedStops = stops.map(s =>
                                          s.stop_id === stop.stop_id ? { ...s, trigger_after_stop_number: v } : s
                                        );
                                        setStops(updatedStops);
                                      }}
                                      data-testid={`edit-trigger-after-${stop.stop_id}`}
                                      className="w-full px-2 py-1.5 rounded border border-indigo-300 text-xs bg-white"
                                      title="Trigger after"
                                    >
                                      <option value="">— Trigger: tour start —</option>
                                      {stops.filter(s => !s.is_hidden).map((s) => (
                                        <option key={s.stop_id} value={s.stop_number}>
                                          After #{s.stop_number} {s.title}
                                        </option>
                                      ))}
                                    </select>
                                    <select
                                      value={stop.expires_when_stop_number ?? ''}
                                      onChange={(e) => {
                                        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                        const updatedStops = stops.map(s =>
                                          s.stop_id === stop.stop_id ? { ...s, expires_when_stop_number: v } : s
                                        );
                                        setStops(updatedStops);
                                      }}
                                      data-testid={`edit-expires-when-${stop.stop_id}`}
                                      className="w-full px-2 py-1.5 rounded border border-indigo-300 text-xs bg-white"
                                      title="Expires when reached"
                                    >
                                      <option value="">— Expires: never —</option>
                                      {stops.filter(s => !s.is_hidden).map((s) => (
                                        <option key={s.stop_id} value={s.stop_number}>
                                          At #{s.stop_number} {s.title}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <input
                                      type="number"
                                      min="0"
                                      step="30"
                                      value={stop.expires_after_seconds ?? ''}
                                      onChange={(e) => {
                                        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                        const updatedStops = stops.map(s =>
                                          s.stop_id === stop.stop_id ? { ...s, expires_after_seconds: v } : s
                                        );
                                        setStops(updatedStops);
                                      }}
                                      data-testid={`edit-expires-after-seconds-${stop.stop_id}`}
                                      placeholder="e.g. 180 sec"
                                      className="w-full px-2 py-1.5 rounded border border-indigo-300 text-xs bg-white"
                                    />
                                    <input
                                      type="text"
                                      maxLength={120}
                                      value={stop.recovery_message || ''}
                                      onChange={(e) => {
                                        const updatedStops = stops.map(s =>
                                          s.stop_id === stop.stop_id ? { ...s, recovery_message: e.target.value } : s
                                        );
                                        setStops(updatedStops);
                                      }}
                                      data-testid={`edit-recovery-message-${stop.stop_id}`}
                                      placeholder="If wrong stop reached: toast text"
                                      className="w-full px-2 py-1.5 rounded border border-indigo-300 text-xs bg-white"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3">
                          <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">Navigation:</label>
                          <select
                            value={stop.navigation_mode || 'walking'}
                            onChange={(e) => {
                              const updatedStops = stops.map(s => 
                                s.stop_id === stop.stop_id ? { ...s, navigation_mode: e.target.value } : s
                              );
                              setStops(updatedStops);
                            }}
                            className="flex-1 px-3 py-2 rounded border border-gray-300 text-sm"
                          >
                            <option value="walking">🚶 Walking - Users walk to this stop</option>
                            <option value="driving">🚗 Driving - Users drive to this stop</option>
                          </select>
                        </div>
                        
                        {/* Include in Express Tour */}
                        {tourData.express_enabled && (
                          <div className="flex items-center gap-3 bg-amber-50 rounded-lg p-2 mt-2">
                            <input
                              type="checkbox"
                              id={`express-${stop.stop_id}`}
                              checked={stop.include_in_express || false}
                              onChange={(e) => {
                                const updatedStops = stops.map(s => 
                                  s.stop_id === stop.stop_id ? { ...s, include_in_express: e.target.checked } : s
                                );
                                setStops(updatedStops);
                              }}
                              className="w-5 h-5 text-amber-600 rounded"
                            />
                            <label htmlFor={`express-${stop.stop_id}`} className="text-sm font-semibold text-amber-800">
                              ⚡ Include in Express Tour
                            </label>
                          </div>
                        )}

                        {/* Directions start timestamp — used by the "Replay
                            directions" button in the tour player. Hidden on
                            the final visible stop (no next-stop to direct to)
                            and on hidden waypoints (they aren't in the numbered
                            sequence). */}
                        {!m.isHidden && !m.isLastInGroup && (
                          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mt-2">
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-sm font-semibold text-teal-900">
                                🧭 Directions start at (mm:ss)
                              </label>
                              <button
                                type="button"
                                onClick={() => {
                                  const el = document.getElementById(`regular-audio-${stop.stop_id}`);
                                  if (!el || !Number.isFinite(el.currentTime)) {
                                    toast.error('Play the audio first, then click Grab');
                                    return;
                                  }
                                  const secs = Math.max(0, Math.floor(el.currentTime));
                                  const updatedStops = stops.map((s) =>
                                    s.stop_id === stop.stop_id
                                      ? { ...s, directions_start_seconds: secs }
                                      : s
                                  );
                                  setStops(updatedStops);
                                  const mm = Math.floor(secs / 60);
                                  const ss = String(secs % 60).padStart(2, '0');
                                  toast.success(`Set directions at ${mm}:${ss}`);
                                }}
                                data-testid={`grab-regular-time-${stop.stop_id}`}
                                className="text-xs font-black text-teal-800 hover:text-teal-950 bg-teal-100 hover:bg-teal-200 px-2 py-1 rounded border border-teal-300"
                                title="Set to the current playhead of the audio preview above"
                              >
                                ⏱ Grab from playhead
                              </button>
                            </div>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="leave empty to default to last 20 sec"
                              value={(() => {
                                const v = stop.directions_start_seconds;
                                if (v == null || v === '') return '';
                                const n = parseInt(v, 10);
                                if (!Number.isFinite(n) || n < 0) return '';
                                const mm = Math.floor(n / 60);
                                const ss = String(n % 60).padStart(2, '0');
                                return `${mm}:${ss}`;
                              })()}
                              onChange={(e) => {
                                // Accept "mm:ss", "m:ss", or raw seconds. Empty = null.
                                const raw = (e.target.value || '').trim();
                                let secs = null;
                                if (raw !== '') {
                                  if (raw.includes(':')) {
                                    const [m, s] = raw.split(':');
                                    const mi = parseInt(m, 10) || 0;
                                    const se = parseInt(s, 10) || 0;
                                    secs = mi * 60 + se;
                                  } else {
                                    const n = parseInt(raw, 10);
                                    secs = Number.isFinite(n) ? n : null;
                                  }
                                }
                                const updatedStops = stops.map((s) =>
                                  s.stop_id === stop.stop_id
                                    ? { ...s, directions_start_seconds: secs }
                                    : s
                                );
                                setStops(updatedStops);
                              }}
                              className="w-full px-3 py-2 rounded border border-teal-300 text-sm bg-white"
                              data-testid={`directions-start-${stop.stop_id}`}
                            />
                            <p className="text-xs text-teal-700 mt-1">
                              The exact second the narrator starts giving directions to the next stop. Leave blank — the &quot;Replay directions&quot; button will jump to the last 20 seconds.
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* GPS Section - Only show if NOT an intro stop */}
                      {!stop.is_intro && (
                        <div className="bg-blue-50 rounded-lg p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-semibold text-gray-700">📍 GPS Location</label>
                          </div>
                          
                          {/* Location Setting Options */}
                          <div className="grid grid-cols-1 gap-2">
                            {/* Option 1: Pick on Map */}
                            <button
                              type="button"
                              onClick={() => {
                                setMapSelectedStopId(stop.stop_id);
                                setShowMap(true);
                                toast.info('Click on the map to set location, or drag the marker');
                                // Scroll to map
                                document.getElementById('map-section')?.scrollIntoView({ behavior: 'smooth' });
                              }}
                              className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded text-sm font-bold flex items-center justify-center gap-2"
                            >
                              <Map className="w-4 h-4" />
                              1. Pick Location on Map
                            </button>
                            
                            {/* Option 2: Search by Address */}
                            <div className="flex gap-2">
                              <input
                                type="text"
                                placeholder="2. Enter address to search..."
                                className="flex-1 px-3 py-2 rounded border border-gray-300 text-sm"
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter') {
                                    const address = e.target.value;
                                    if (!address) return;
                                    try {
                                      const geocoder = new window.google.maps.Geocoder();
                                      geocoder.geocode({ address }, (results, status) => {
                                        if (status === 'OK' && results[0]) {
                                          const lat = results[0].geometry.location.lat();
                                          const lng = results[0].geometry.location.lng();
                                          const updatedStops = stops.map(s => 
                                            s.stop_id === stop.stop_id ? { ...s, latitude: lat, longitude: lng } : s
                                          );
                                          setStops(updatedStops);
                                          toast.success(`Location set: ${results[0].formatted_address}`);
                                        } else {
                                          toast.error('Address not found');
                                        }
                                      });
                                    } catch (err) {
                                      toast.error('Failed to search address');
                                    }
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  const input = e.target.previousSibling;
                                  const address = input.value;
                                  if (!address) {
                                    toast.error('Enter an address first');
                                    return;
                                  }
                                  try {
                                    const geocoder = new window.google.maps.Geocoder();
                                    geocoder.geocode({ address }, (results, status) => {
                                      if (status === 'OK' && results[0]) {
                                        const lat = results[0].geometry.location.lat();
                                        const lng = results[0].geometry.location.lng();
                                        const updatedStops = stops.map(s => 
                                          s.stop_id === stop.stop_id ? { ...s, latitude: lat, longitude: lng } : s
                                        );
                                        setStops(updatedStops);
                                        toast.success(`Location set: ${results[0].formatted_address}`);
                                      } else {
                                        toast.error('Address not found');
                                      }
                                    });
                                  } catch (err) {
                                    toast.error('Failed to search address');
                                  }
                                }}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-sm font-bold"
                              >
                                <Search className="w-4 h-4" />
                              </button>
                            </div>
                            
                            {/* Option 3: Paste WhatsApp/Google Maps Link */}
                            <div className="flex gap-2">
                              <input
                                type="text"
                                id={`map-link-input-${stop.stop_id}`}
                                placeholder="3. Paste Google Maps/WhatsApp link..."
                                className="flex-1 px-3 py-2 rounded border border-gray-300 text-sm"
                              />
                              <button
                                type="button"
                                onClick={async () => {
                                  const input = document.getElementById(`map-link-input-${stop.stop_id}`);
                                  const url = input?.value?.trim();
                                  if (!url) {
                                    toast.error('Please paste a link first');
                                    return;
                                  }
                                  
                                  // First try to parse coordinates locally
                                  let lat = null, lng = null;
                                  
                                  // Format: 32.0853, 34.7818 or 32.0853,34.7818
                                  const coordMatch = url.match(/(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)/);
                                  if (coordMatch && !url.includes('http')) {
                                    lat = parseFloat(coordMatch[1]);
                                    lng = parseFloat(coordMatch[2]);
                                  }
                                  
                                  // Format: https://maps.google.com/?q=32.0853,34.7818
                                  const googleMapsMatch = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                                  if (googleMapsMatch) {
                                    lat = parseFloat(googleMapsMatch[1]);
                                    lng = parseFloat(googleMapsMatch[2]);
                                  }
                                  
                                  // Format: https://www.google.com/maps/@32.0853,34.7818
                                  const googleMapsAtMatch = url.match(/maps\/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                                  if (googleMapsAtMatch) {
                                    lat = parseFloat(googleMapsAtMatch[1]);
                                    lng = parseFloat(googleMapsAtMatch[2]);
                                  }
                                  
                                  // If we found coordinates locally, use them
                                  if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
                                    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                                      const updatedStops = stops.map(s => 
                                        s.stop_id === stop.stop_id ? { ...s, latitude: lat, longitude: lng } : s
                                      );
                                      setStops(updatedStops);
                                      toast.success(`Location set: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
                                      input.value = '';
                                      return;
                                    }
                                  }
                                  
                                  // For short links (goo.gl, maps.app.goo.gl), call backend to resolve
                                  if (url.includes('goo.gl') || url.includes('maps.app')) {
                                    try {
                                      toast.info('Resolving link...');
                                      const response = await axios.post(`${API}/admin/resolve-map-link`, { url });
                                      const { latitude, longitude } = response.data;
                                      
                                      const updatedStops = stops.map(s => 
                                        s.stop_id === stop.stop_id ? { ...s, latitude, longitude } : s
                                      );
                                      setStops(updatedStops);
                                      toast.success(`Location set: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
                                      input.value = '';
                                    } catch (error) {
                                      console.error('Failed to resolve link:', error);
                                      toast.error(error.response?.data?.detail || 'Failed to resolve link');
                                    }
                                  } else {
                                    toast.error('Could not extract coordinates from this link');
                                  }
                                }}
                                className="bg-cyan-500 hover:bg-cyan-600 text-white px-3 py-2 rounded text-sm font-bold"
                              >
                                Set Location
                              </button>
                            </div>
                            <p className="text-xs text-gray-500">
                              Supports: maps.app.goo.gl links, Google Maps links, coordinates (lat, lng)
                            </p>
                            
                            {/* Option 4: Use My Current Location */}
                            <button
                              type="button"
                              onClick={() => getCurrentLocationForStop(stop.stop_id)}
                              disabled={gettingLocation}
                              className="bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white px-3 py-2 rounded text-sm font-bold flex items-center justify-center gap-2"
                            >
                              {gettingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                              4. Use My Current Location
                            </button>
                          </div>
                          
                          {/* Current Coordinates Display */}
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <div>
                              <label className="text-xs text-gray-500">Latitude</label>
                              <input
                                type="number"
                                step="any"
                                value={stop.latitude || ''}
                                onChange={(e) => {
                                  const updatedStops = stops.map(s => 
                                    s.stop_id === stop.stop_id ? { ...s, latitude: e.target.value } : s
                                  );
                                  setStops(updatedStops);
                                }}
                                className="w-full px-3 py-2 rounded border border-gray-300 text-sm"
                                placeholder="Latitude"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Longitude</label>
                              <input
                                type="number"
                                step="any"
                                value={stop.longitude || ''}
                                onChange={(e) => {
                                  const updatedStops = stops.map(s => 
                                    s.stop_id === stop.stop_id ? { ...s, longitude: e.target.value } : s
                                  );
                                  setStops(updatedStops);
                                }}
                                className="w-full px-3 py-2 rounded border border-gray-300 text-sm"
                                placeholder="Longitude"
                              />
                            </div>
                          </div>
                          {stop.latitude && stop.longitude && (
                            <p className="text-xs text-green-600 font-semibold">
                              ✓ Location set: {parseFloat(stop.latitude).toFixed(6)}, {parseFloat(stop.longitude).toFixed(6)}
                            </p>
                          )}
                          
                          {/* Radius with Smart Suggestions */}
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">📏 Trigger Radius</label>
                            
                            {/* Smart suggestion for this stop */}
                            {(() => {
                              const suggestion = getRadiusSuggestion(
                                parseFloat(stop.latitude), 
                                parseFloat(stop.longitude),
                                stop.stop_id
                              );
                              const overlaps = stop.latitude && stop.longitude 
                                ? checkRadiusOverlap(
                                    parseFloat(stop.latitude), 
                                    parseFloat(stop.longitude), 
                                    parseInt(stop.radius_meters) || 50,
                                    stop.stop_id
                                  ) 
                                : [];
                              
                              return (
                                <>
                                  {suggestion.nearestStop && (
                                    <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-2 text-xs">
                                      <p className="text-blue-800">
                                        Nearest: "{suggestion.nearestStop.title}" ({suggestion.nearestDistance}m away)
                                      </p>
                                      <p className="text-blue-600">
                                        Max suggested: <strong>{suggestion.suggestedMax}m</strong>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updatedStops = stops.map(s => 
                                              s.stop_id === stop.stop_id ? { ...s, radius_meters: suggestion.suggestedMax } : s
                                            );
                                            setStops(updatedStops);
                                          }}
                                          className="ml-2 text-blue-700 underline hover:text-blue-900"
                                        >
                                          Apply
                                        </button>
                                      </p>
                                    </div>
                                  )}
                                  
                                  {overlaps.length > 0 && (
                                    <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-xs">
                                      <div className="flex items-center gap-1 text-amber-800">
                                        <AlertTriangle className="w-4 h-4" />
                                        <span className="font-semibold">Overlap warning:</span>
                                      </div>
                                      {overlaps.map((o, i) => (
                                        <p key={i} className="text-amber-700">• "{o.stop.title}" (overlaps by {o.overlapAmount}m)</p>
                                      ))}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                            
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={stop.radius_meters || 50}
                                onChange={(e) => {
                                  const updatedStops = stops.map(s => 
                                    s.stop_id === stop.stop_id ? { ...s, radius_meters: parseInt(e.target.value) || 50 } : s
                                  );
                                  setStops(updatedStops);
                                }}
                                className="w-24 px-3 py-2 rounded border border-gray-300 text-sm text-center font-bold"
                                placeholder="Radius"
                              />
                              <span className="text-sm text-gray-600">meters</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Audio Upload for Stop */}
                      <div>
                        {!stop.audio_url ? (
                          <label className="w-full bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-bold cursor-pointer flex items-center justify-center transition-all">
                            <Upload className="w-5 h-5 mr-2" />
                            Upload Stop Audio
                            <input
                              type="file"
                              accept="audio/*,.mp3,.wav,.m4a,.ogg"
                              onChange={(e) => handleStopAudioUpload(e, stop.stop_id)}
                              className="hidden"
                            />
                          </label>
                        ) : (
                          <div className="bg-green-50 border border-green-300 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-green-700 flex items-center">
                                <Music className="w-4 h-4 mr-1" /> Audio Uploaded
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedStops = stops.map(s => 
                                    s.stop_id === stop.stop_id ? { ...s, audio_url: '' } : s
                                  );
                                  setStops(updatedStops);
                                }}
                                className="text-red-500 hover:text-red-700 text-xs font-bold"
                              >
                                Remove
                              </button>
                            </div>
                            <audio 
                              controls 
                              className="w-full h-10"
                              id={`regular-audio-${stop.stop_id}`}
                              data-testid={`regular-audio-preview-${stop.stop_id}`}
                              onError={(e) => {
                                console.error('Audio load error:', stop.audio_url);
                                toast.error(`Audio failed to load. The file may have been deleted.`);
                              }}
                            >
                              <source src={
                                stop.audio_url.startsWith('http') 
                                  ? stop.audio_url 
                                  : stop.audio_url.startsWith('/api') 
                                    ? `${BACKEND_URL}${stop.audio_url}`
                                    : stop.audio_url.startsWith('/')
                                      ? `${BACKEND_URL}${stop.audio_url}`
                                      : stop.audio_url
                              } type="audio/mpeg" />
                            </audio>
                          </div>
                        )}
                      </div>
                      
                      {/* Express Audio Upload - Only show if express is enabled and stop is included */}
                      {tourData.express_enabled && stop.include_in_express && (
                        <div className="mt-3 border-t border-amber-200 pt-3">
                          <p className="text-sm font-semibold text-amber-700 mb-2">⚡ Express Tour Audio (optional - uses main audio if not set)</p>
                          {!stop.express_audio_url ? (
                            <label className="w-full bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-bold cursor-pointer flex items-center justify-center transition-all text-sm">
                              <Upload className="w-4 h-4 mr-2" />
                              Upload Express Audio
                              <input
                                type="file"
                                accept="audio/*,.mp3,.wav,.m4a,.ogg"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  setUploading(true);
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  try {
                                    const response = await axios.post(`${API}/admin/audio/upload`, formData, {
                                      headers: { 'Content-Type': 'multipart/form-data' }
                                    });
                                    const expressAudioUrl = `${BACKEND_URL}${response.data.audio_url}`;
                                    
                                    // Auto-save to database immediately
                                    await axios.put(`${API}/admin/stops/${stop.stop_id}`, {
                                      express_audio_url: expressAudioUrl
                                    });
                                    
                                    const updatedStops = stops.map(s => 
                                      s.stop_id === stop.stop_id ? { ...s, express_audio_url: expressAudioUrl } : s
                                    );
                                    setStops(updatedStops);
                                    toast.success('Express audio uploaded and saved');
                                  } catch (error) {
                                    toast.error('Failed to upload express audio');
                                  } finally {
                                    setUploading(false);
                                  }
                                }}
                                className="hidden"
                              />
                            </label>
                          ) : (
                            <div className="bg-amber-50 border border-amber-300 rounded-lg p-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-amber-700">⚡ Express Audio</span>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      // Auto-save removal to database immediately
                                      await axios.put(`${API}/admin/stops/${stop.stop_id}`, {
                                        express_audio_url: null
                                      });
                                      const updatedStops = stops.map(s => 
                                        s.stop_id === stop.stop_id ? { ...s, express_audio_url: '' } : s
                                      );
                                      setStops(updatedStops);
                                      toast.success('Express audio removed');
                                    } catch (error) {
                                      toast.error('Failed to remove express audio');
                                    }
                                  }}
                                  className="text-red-500 hover:text-red-700 text-xs font-bold"
                                >
                                  Remove
                                </button>
                              </div>
                              <audio
                                controls
                                className="w-full h-8"
                                id={`express-audio-${stop.stop_id}`}
                                data-testid={`express-audio-preview-${stop.stop_id}`}
                              >
                                <source src={stop.express_audio_url} type="audio/mpeg" />
                              </audio>
                              {/* Express-specific directions timecode. Independent
                                  from the regular-audio timecode because express
                                  audio is a different file with different timing. */}
                              <div className="mt-2 pt-2 border-t border-amber-200">
                                <div className="flex items-center justify-between mb-1">
                                  <label className="block text-xs font-semibold text-amber-900">
                                    🧭 Express directions start at (mm:ss)
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const el = document.getElementById(`express-audio-${stop.stop_id}`);
                                      if (!el || !Number.isFinite(el.currentTime)) {
                                        toast.error('Play the audio first, then click Grab');
                                        return;
                                      }
                                      const secs = Math.max(0, Math.floor(el.currentTime));
                                      const updatedStops = stops.map((s) =>
                                        s.stop_id === stop.stop_id
                                          ? { ...s, express_directions_start_seconds: secs }
                                          : s
                                      );
                                      setStops(updatedStops);
                                      const mm = Math.floor(secs / 60);
                                      const ss = String(secs % 60).padStart(2, '0');
                                      toast.success(`Set express directions at ${mm}:${ss}`);
                                    }}
                                    data-testid={`grab-express-time-${stop.stop_id}`}
                                    className="text-xs font-black text-amber-800 hover:text-amber-950 bg-amber-100 hover:bg-amber-200 px-2 py-1 rounded border border-amber-300"
                                    title="Set to the current playhead of the audio preview above"
                                  >
                                    ⏱ Grab from playhead
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  placeholder="leave empty for last 20 sec"
                                  value={(() => {
                                    const v = stop.express_directions_start_seconds;
                                    if (v == null || v === '') return '';
                                    const n = parseInt(v, 10);
                                    if (!Number.isFinite(n) || n < 0) return '';
                                    const mm = Math.floor(n / 60);
                                    const ss = String(n % 60).padStart(2, '0');
                                    return `${mm}:${ss}`;
                                  })()}
                                  onChange={(e) => {
                                    const raw = (e.target.value || '').trim();
                                    let secs = null;
                                    if (raw !== '') {
                                      if (raw.includes(':')) {
                                        const [m, s] = raw.split(':');
                                        secs = (parseInt(m, 10) || 0) * 60 + (parseInt(s, 10) || 0);
                                      } else {
                                        const n = parseInt(raw, 10);
                                        secs = Number.isFinite(n) ? n : null;
                                      }
                                    }
                                    const updatedStops = stops.map((s) =>
                                      s.stop_id === stop.stop_id
                                        ? { ...s, express_directions_start_seconds: secs }
                                        : s
                                    );
                                    setStops(updatedStops);
                                  }}
                                  className="w-full px-2 py-1.5 rounded border border-amber-300 text-sm bg-white"
                                  data-testid={`express-directions-start-${stop.stop_id}`}
                                />
                                <p className="text-xs text-amber-700 mt-1">
                                  The second where the narrator starts giving directions to the next stop, in the <strong>express</strong> audio. Independent from the regular audio timecode above.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => {
                            // Get the latest stop data from state to avoid stale closure
                            const latestStop = stops.find(s => s.stop_id === stop.stop_id) || stop;
                            handleUpdateStop(latestStop);
                          }}
                          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center"
                        >
                          <Check className="w-4 h-4 mr-1" /> Save Changes
                        </button>
                        <button
                          onClick={() => { setEditingStopId(null); fetchStops(); }}
                          className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-full text-sm font-bold flex items-center"
                        >
                          <X className="w-4 h-4 mr-1" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        {/* Selection checkbox — only visible in selection mode. */}
                        {selectionMode && (
                          <label
                            className="flex items-center justify-center mr-3 cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedStopIds.includes(stop.stop_id)}
                              onChange={(e) => {
                                setSelectedStopIds((prev) => {
                                  if (e.target.checked) return [...prev, stop.stop_id];
                                  return prev.filter((id) => id !== stop.stop_id);
                                });
                              }}
                              data-testid={`select-stop-${stop.stop_id}`}
                              className="w-5 h-5 accent-slate-900"
                            />
                          </label>
                        )}
                        {/* Drag Handle */}
                        <div 
                          className="flex items-center justify-center mr-2 cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded transition-colors"
                          title="Drag to reorder"
                        >
                          <GripVertical className="w-5 h-5 text-gray-400" />
                        </div>
                        
                        {/* Reorder Buttons */}
                        <div className="flex flex-col mr-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); moveStop(stop.stop_id, 'up'); }}
                            disabled={m.isFirstInGroup}
                            className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                            title="Move up"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); moveStop(stop.stop_id, 'down'); }}
                            disabled={m.isLastInGroup}
                            className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                            title="Move down"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mr-3 text-white ${
                            m.isHidden ? (stop.hidden_kind === 'warn' ? 'bg-amber-600' : 'bg-indigo-600') : 'bg-gray-800'
                          }`}
                          data-testid={`stop-badge-${stop.stop_id}`}
                          title={m.isHidden ? 'Hidden waypoint — outside the numbered tour sequence' : 'Stop number'}
                        >
                          {m.label}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900">{stop.title}</p>
                          <p className="text-sm text-gray-600">
                            {stop.latitude && stop.longitude 
                              ? `${parseFloat(stop.latitude).toFixed(4)}, ${parseFloat(stop.longitude).toFixed(4)}`
                              : 'No GPS (Intro stop)'}
                          </p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {stop.is_intro && (
                              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-semibold">
                                🎙️ Intro
                              </span>
                            )}
                            {stop.is_hidden && (
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                  stop.hidden_kind === 'warn'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-indigo-100 text-indigo-800'
                                }`}
                                title="Hidden waypoint — not shown in the public tour list"
                              >
                                🔒 {stop.hidden_kind === 'warn' ? 'Off-track' : 'Encouragement'}
                              </span>
                            )}
                            {/* Admin-only eligibility legend for hidden waypoints
                                — shows the contextual window at a glance so you
                                can audit for false-alarm risk. */}
                            {stop.is_hidden && (stop.trigger_after_stop_number != null || stop.expires_when_stop_number != null || stop.expires_after_seconds != null) && (
                              <span
                                className="text-xs px-2 py-0.5 rounded-full font-mono bg-slate-100 text-slate-700 border border-slate-200"
                                data-testid={`eligibility-legend-${stop.stop_id}`}
                                title="Contextual eligibility window (admin only, invisible to users)"
                              >
                                🔗 {stop.trigger_after_stop_number != null ? `after #${stop.trigger_after_stop_number}` : 'from start'}
                                {' → '}
                                {stop.expires_when_stop_number != null ? `expires #${stop.expires_when_stop_number}` : 'no stop expiry'}
                                {stop.expires_after_seconds != null ? ` · ${stop.expires_after_seconds}s` : ''}
                              </span>
                            )}
                            {!stop.is_intro && stop.radius_meters && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">
                                📏 {stop.radius_meters}m radius
                              </span>
                            )}
                            {stop.navigation_mode === 'driving' && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                                🚗 Drive
                              </span>
                            )}
                            {stop.include_in_express && tourData.express_enabled && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">
                                ⚡ Express
                              </span>
                            )}
                            {stop.audio_url ? (
                              <span className="text-xs text-green-600 flex items-center">
                                <Music className="w-3 h-3 mr-1" /> Audio linked
                              </span>
                            ) : (
                              <span className="text-xs text-orange-600 flex items-center">
                                <Music className="w-3 h-3 mr-1" /> No audio yet
                              </span>
                            )}
                          </div>
                          {/* Audio Preview Player */}
                          {stop.audio_url && (
                            <div className="mt-2">
                              <audio 
                                controls 
                                className="w-full h-8" 
                                style={{maxWidth: '250px'}}
                                onError={() => toast.error('Audio file not found. Please re-upload.')}
                              >
                                <source src={
                                  stop.audio_url.startsWith('http') 
                                    ? stop.audio_url 
                                    : `${BACKEND_URL}${stop.audio_url}`
                                } type="audio/mpeg" />
                              </audio>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!stop.audio_url && (
                          <label className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold cursor-pointer flex items-center">
                            <Upload className="w-4 h-4 mr-1" />
                            Upload Audio
                            <input
                              type="file"
                              accept="audio/*"
                              onChange={(e) => handleStopAudioUpload(e, stop.stop_id)}
                              className="hidden"
                            />
                          </label>
                        )}
                        <button
                          onClick={() => setEditingStopId(stop.stop_id)}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-full"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteStop(stop.stop_id)}
                          className="bg-red-100 hover:bg-red-200 text-red-600 p-2 rounded-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                </React.Fragment>
                );
              })}
            </div>
            );
          })()}
          
          <p className="text-sm text-gray-500 mt-4 bg-gray-100 p-3 rounded-lg">
            💡 <strong>Tip:</strong> Green border = audio linked. Orange border = needs audio. Click the audio player to preview!
          </p>

          <button
            onClick={handleSanitizeStopNumbers}
            data-testid="sanitize-stop-numbers-button"
            className="mt-3 text-xs text-slate-500 hover:text-orange-600 underline"
            title="Renumbers visible stops 1..N and hidden waypoints in the reserved range. Fixes drag-and-drop corruption."
          >
            Sanitize stop numbering (heal order)
          </button>
          <button
            onClick={handleDiagnoseTour}
            disabled={diagnoseLoading}
            data-testid="diagnose-tour-button"
            className="mt-3 ml-4 text-xs text-slate-500 hover:text-blue-600 underline disabled:opacity-50"
            title="Inspects this tour + its stops and reports any data issues that could stop the player from opening."
          >
            {diagnoseLoading ? 'Diagnosing…' : 'Diagnose this tour (why won\'t it play?)'}
          </button>
          {diagnoseResult && (
            <div
              data-testid="diagnose-result"
              className={`mt-4 p-4 rounded-lg border-2 ${
                diagnoseResult.verdict === 'healthy'
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                  : 'bg-red-50 border-red-400 text-red-900'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="font-bold">
                  {diagnoseResult.verdict === 'healthy' ? '✅ Tour is healthy' : '⚠️ Tour has issues'}
                  <span className="ml-3 text-xs opacity-70 font-normal">
                    {diagnoseResult.total_stops} stops
                    ({diagnoseResult.visible_count} visible / {diagnoseResult.hidden_count} hidden)
                    · active={String(diagnoseResult.is_active)}
                    · express={String(diagnoseResult.express_enabled)}
                  </span>
                </p>
                <button
                  onClick={() => setDiagnoseResult(null)}
                  className="text-xs underline opacity-70 hover:opacity-100"
                >
                  dismiss
                </button>
              </div>
              {diagnoseResult.issues.length === 0 ? (
                <p className="text-sm">No issues detected. If the tour still won't play, check the browser DevTools Network tab when clicking Play/Test.</p>
              ) : (
                <ul className="text-sm space-y-2 mt-2">
                  {diagnoseResult.issues.map((issue, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="font-mono text-xs uppercase font-bold flex-shrink-0">
                        [{issue.level}]
                      </span>
                      <span>{issue.msg}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <button
          onClick={saveTour}
          disabled={saving}
          data-testid="save-tour-button"
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white py-5 rounded-full font-black text-2xl uppercase shadow-xl hover:shadow-2xl transition-all active:scale-95 disabled:cursor-not-allowed tracking-wide flex items-center justify-center"
        >
          <Save className="w-6 h-6 mr-3" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </main>

      {/* Floating action bar when stops are selected in selection mode */}
      {selectionMode && selectedStopIds.length > 0 && (
        <div className="fixed bottom-24 sm:bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-slate-900 text-white rounded-full shadow-2xl px-4 py-3 flex items-center gap-3 border border-slate-700" data-testid="selection-toolbar">
          <span className="text-sm font-bold pl-2">
            {selectedStopIds.length} stop{selectedStopIds.length === 1 ? '' : 's'} selected
          </span>
          <button
            onClick={() => setSelectedStopIds([])}
            className="text-xs text-slate-300 hover:text-white underline"
          >
            Clear
          </button>
          <button
            onClick={() => setShowSplitMove(true)}
            data-testid="split-move-button"
            className="inline-flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-4 py-2 text-sm active:scale-95 transition"
          >
            <MoveRight className="w-4 h-4" />
            Split / Move Stops
          </button>
        </div>
      )}

      {/* Split / Move dialog — hydrated with the current selection preserved
          in stop_number order so the target tour receives the stops in the
          same sequence as they appear in the source list. */}
      <SplitMoveStopsDialog
        open={showSplitMove}
        onClose={() => setShowSplitMove(false)}
        sourceTour={{ tour_id: tourId, title: tourData?.title || '' }}
        selectedStops={
          selectedStopIds
            .map((id) => stops.find((s) => s.stop_id === id))
            .filter(Boolean)
            .sort((a, b) => (a.stop_number || 0) - (b.stop_number || 0))
        }
        onSuccess={async (result) => {
          setShowSplitMove(false);
          setSelectionMode(false);
          setSelectedStopIds([]);
          // Refetch stops so the source tour reflects the move
          try {
            const { data } = await axios.get(`${API}/admin/tours/${tourId}/stops`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            setStops(data || []);
          } catch (_) { /* silent */ }
          if (result?.created_new_tour) {
            // Ask before navigating away — the admin may want to keep editing
            // the source tour, so we surface a toast with a link instead of
            // silently redirecting.
            toast.success('New tour created with your stops', {
              action: {
                label: 'Open new tour',
                onClick: () => navigate(`/admin/edit-tour/${result.destination_tour_id}`),
              },
              duration: 8000,
            });
          }
        }}
      />

      {/* Deploy indicator — tiny footer so admins can verify at a glance
          whether the current build includes recent fixes. Refreshes on
          every backend restart (i.e. every "Re-publish changes"). */}
      {buildInfo && (
        <div className="text-center text-[10px] text-slate-400 py-3" data-testid="build-info-footer">
          Build: {buildInfo.features_marker} · Backend restarted{' '}
          {(() => {
            try {
              const started = new Date(buildInfo.backend_started_at);
              const secs = Math.max(0, Math.floor((Date.now() - started.getTime()) / 1000));
              if (secs < 60) return `${secs}s ago`;
              if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
              const hrs = Math.floor(secs / 3600);
              return hrs < 24 ? `${hrs}h ago` : `${Math.floor(hrs / 24)}d ago`;
            } catch { return 'just now'; }
          })()}
        </div>
      )}
    </div>
  );
};

export default EditTourPage;